import { useOktaAuth } from "@okta/okta-react";
import {
  createContext,
  ReactElement,
  useContext,
  useEffect,
  useReducer,
  useState,
} from "react";
import { isJDUser } from "./RBACAccessCheck";
import { isFeatureToggleRBACEnabled } from "./RBACAcessUtil";
import DealerApi, { Account } from "../../api/dealer/DealerApi";
import useDealerApi from "../../api/dealer/useDealerApi";
import { FeatureToggleApi } from "../../api/featureToggle/featureToggleApi";
import FeatureToggleApiV2 from "../../api/featureToggle/featureToggleApiV2";
import RbacApi from "../../api/rbac/rbacApi";
import {
  BffRBACRolesResponse,
  BusinessAction,
  RBACRole,
  RBACRolesResponse,
} from "../../api/rbac/types";
import { FeatureToggleDetail } from "../../pages/featureToggles/types";
import { ConfigContext } from "../ConfigContext";
import {
  DelegateContext,
  useEffectiveUserId,
} from "../DelegateContext/DelegateContext";
import {
  setAccountDetailsAction,
  setDataLoadedAction,
  setFeatureToggleDetailsAction,
  setUserRoleDetailsAction,
  updateProcessedAction,
} from "./reducer/action";
import {
  getInitialState,
  listRBACAccessContextReducer,
} from "./reducer/reducer";
import {
  accountTogglesPromise,
  canValidateRBACForDealerFacingRole,
  featureTogglesPromise,
  isRBACEnabledForDealerRoles,
  resolvePromise,
  userDetailsPromise,
} from "./reducer/selector";
import xtremeBffApi from "../../api/xtremeBff/xtremeBffApi";

export interface RBACAccessContextProps {
  userDetails: Promise<RBACRolesResponse>;
  featureToggleDetails: Promise<FeatureToggleDetail[]>;
  mappingAccounts: Promise<{ data: Account[] }>;
  primaryAccount: Account | undefined;
  isDataLoaded: boolean;
  isRBACEnabled: boolean;
  isCorporateViewEnabled: boolean;
  hasMappingError: boolean;
  hasNoRoles: boolean;
}

export const RBACAccessContext = createContext({} as RBACAccessContextProps);

const getUserRoles = async (
  RBAC_SERVICE_URL: string,
  userXid: string,
  token: string
): Promise<RBACRolesResponse> => {
  const response: RBACRolesResponse = await RbacApi.getUserRoles(
    RBAC_SERVICE_URL,
    token,
    userXid
  );
  return response;
};

export const getUserRolesV2 = async (
  XTREME_BFF_SERVICE_URL: string,
  accessToken: any,
  idToken: any,
  delegatedUserId?: string
): Promise<RBACRolesResponse> => {
  const bffRbacResponse: BffRBACRolesResponse = delegatedUserId
    ? await xtremeBffApi.getRbacMappings(
        XTREME_BFF_SERVICE_URL,
        accessToken,
        idToken,
        delegatedUserId
      )
    : await xtremeBffApi.getRbacMappings(
        XTREME_BFF_SERVICE_URL,
        accessToken,
        idToken
      );

  const rbacRolesResponse: RBACRolesResponse = {
    userId: bffRbacResponse?.userId,
    roles: bffRbacResponse?.roles as RBACRole[],
    businessActivities: bffRbacResponse?.businessActivities as BusinessAction[],
  };
  return Promise.resolve(rbacRolesResponse);
};

const getAccountDetails = async (
  DEALER_SERVICE_URL: string,
  userXid: string,
  token: string,
  isDealerServiceApiAuthEnable: boolean,
  dealerApiHook: ReturnType<typeof useDealerApi>,
  isClientUI: boolean
): Promise<{ data: Account[] }> => {
  try {
    const accountsMapping: { data: Account[] } = isDealerServiceApiAuthEnable
      ? await dealerApiHook.accountsLoader(DEALER_SERVICE_URL, userXid)
      : await DealerApi.accountsLoader(
          DEALER_SERVICE_URL,
          userXid,
          token,
          isClientUI
        );
    return accountsMapping;
  } catch (error: any) {
    console.error("[RBACAccessContext] Error fetching account details:", error);
    console.error("[RBACAccessContext] Error response:", {
      status: error?.response?.status,
      message: error?.response?.data?.message,
      errorType: error?.response?.data?.error,
    });

    const errorMessage = error.response?.data?.message?.toLowerCase() || "";
    const isInactiveUserError =
      errorMessage.includes("inactive") ||
      errorMessage.includes("user id is inactive") ||
      error.response?.status === 400;

    if (isInactiveUserError) {
      console.warn(
        "[RBACAccessContext] User account is inactive - returning empty accounts"
      );
      return { data: [] };
    }

    throw error;
  }
};

const getPrimaryAccount = (
  multipleDealerAccounts: PromiseSettledResult<{ data: Account[] }> | undefined
) => {
  if (!multipleDealerAccounts) {
    console.error("Dealer accounts result is unavailable.");
  } else if (multipleDealerAccounts.status === "fulfilled") {
    const accounts = multipleDealerAccounts.value.data;

    const accountListHasPrimaryAccount = accounts.filter(
      (account) => account.isPrimaryAccount
    );
    if (accountListHasPrimaryAccount.length > 0) {
      return accountListHasPrimaryAccount.map((account) => account)[0];
    }
  } else if (multipleDealerAccounts.status === "rejected") {
    console.error(
      "Unable to fetch primary account details:",
      multipleDealerAccounts && multipleDealerAccounts.reason
    );
  }
  return "";
};

export const RBACAccessProvider = ({
  children,
}: {
  children: ReactElement;
}) => {
  const { authState } = useOktaAuth();
  const token = authState?.accessToken?.accessToken as string;
  const userXid = authState?.accessToken?.claims?.userID as string;
  const idToken = authState?.idToken?.idToken as string;
  const {
    RBAC_SERVICE_URL,
    FEATURE_TOGGLE_SERVICE_URL,
    DEALER_SERVICE_URL,
    ENABLE_FT_RBAC_INTEGRATION,
    ENABLE_CORPORATE_VIEW,
    ENABLE_RBAC_AUTH_SUPPORT,
    ENABLE_RBAC_AUTH_SUPPORT_FOR_FEATURE_TOGGLE_SERVICE,
    ENABLE_RBAC_AUTH_SUPPORT_FOR_DEALER_SERVICE,
    ENABLE_RBAC_ROLES_V2,
    XTREME_BFF_SERVICE_URL,
    ENABLE_MULTIPLE_ACCOUNTS,
    ENABLE_CORPORATE_VIEW_DPA_ROLE,
    ENABLE_RBAC_NO_ROLES_HANDLING,
    ENABLE_DELEGATE_LOGIN,
  } = useContext(ConfigContext);
  const { isDelegating, delegatedUserId } = useContext(DelegateContext);
  const effectiveUserId = useEffectiveUserId();
  const delegatedUserIdForBff =
    ENABLE_DELEGATE_LOGIN && isDelegating ? delegatedUserId! : undefined;

  const [primaryAccount, setPrimaryAccount] = useState<Account>();
  const [hasMappingError, setHasMappingError] = useState(false);
  const [hasNoRoles, setHasNoRoles] = useState(false);

  const dealerApiHook = useDealerApi();
  const isDealerServiceApiAuthEnable =
    ENABLE_RBAC_AUTH_SUPPORT && ENABLE_RBAC_AUTH_SUPPORT_FOR_DEALER_SERVICE;

  const isFeatureToggleApiAuthEnable =
    ENABLE_RBAC_AUTH_SUPPORT &&
    ENABLE_RBAC_AUTH_SUPPORT_FOR_FEATURE_TOGGLE_SERVICE;

  const enableFTRBAC = isFeatureToggleRBACEnabled(ENABLE_FT_RBAC_INTEGRATION);

  const [state, dispatch] = useReducer(
    listRBACAccessContextReducer,
    getInitialState(
      ENABLE_FT_RBAC_INTEGRATION,
      ENABLE_CORPORATE_VIEW,
      enableFTRBAC
    )
  );

  const featureToggleApi = FeatureToggleApiV2();

  const getFeatureToggles = async (): Promise<FeatureToggleDetail[]> => {
    const response: FeatureToggleDetail[] = isFeatureToggleApiAuthEnable
      ? await featureToggleApi.getFeatureToggles()
      : await FeatureToggleApi.getFeatureToggles(
          FEATURE_TOGGLE_SERVICE_URL,
          token
        );
    return response;
  };

  const fetchUserRolesAndFeatureToggleParallel = async (): Promise<
    [
      PromiseSettledResult<RBACRolesResponse>,
      PromiseSettledResult<FeatureToggleDetail[]>
    ]
  > => {
    const response: [
      PromiseSettledResult<RBACRolesResponse> | PromiseRejectedResult,
      PromiseSettledResult<FeatureToggleDetail[]> | PromiseRejectedResult
    ] = await Promise.allSettled([
      ENABLE_RBAC_ROLES_V2
        ? getUserRolesV2(
            XTREME_BFF_SERVICE_URL,
            token,
            idToken,
            delegatedUserIdForBff
          )
        : getUserRoles(RBAC_SERVICE_URL, effectiveUserId, token),
      getFeatureToggles(),
    ]);
    return response;
  };

  const skipApiCalls = () =>
    !enableFTRBAC ||
    userXid === null ||
    userXid === undefined ||
    (ENABLE_RBAC_ROLES_V2 && !idToken);

  useEffect(() => {
    const fetchDetails = async () => {
      if (skipApiCalls()) return;

      const [userRolesResult, featureTogglesResult]: [
        PromiseSettledResult<RBACRolesResponse> | PromiseRejectedResult,
        PromiseSettledResult<FeatureToggleDetail[]> | PromiseRejectedResult
      ] = await fetchUserRolesAndFeatureToggleParallel();
      dispatch(setUserRoleDetailsAction(userRolesResult));
      dispatch(setFeatureToggleDetailsAction(featureTogglesResult));

      if (isJDUser(resolvePromise(userRolesResult))) {
        const adminAction = updateProcessedAction(
          true,
          ENABLE_CORPORATE_VIEW,
          true,
          true
        );
        dispatch(adminAction);
        return;
      }

      const userRoles = resolvePromise(userRolesResult);

      // Handle no roles case - skip account mapping call
      if (
        ENABLE_RBAC_NO_ROLES_HANDLING &&
        userRoles &&
        userRoles.roles.length === 0
      ) {
        setHasNoRoles(true);
        setHasMappingError(true);
        dispatch(
          setAccountDetailsAction({
            status: "fulfilled",
            value: { data: [] },
          })
        );
        dispatch(updateProcessedAction(false, false, true, true));
        return;
      }

      if (
        ENABLE_CORPORATE_VIEW_DPA_ROLE &&
        userRoles &&
        userRoles.roles.length === 1 &&
        userRoles.roles.includes(RBACRole.CORPORATE_VIEW)
      ) {
        setHasMappingError(true);
        dispatch(
          setAccountDetailsAction({
            status: "fulfilled",
            value: { data: [] },
          })
        );
        dispatch(updateProcessedAction(false, true, true, true));
        return;
      }

      const [accountDetailsResult]: [
        PromiseSettledResult<{ data: Account[] }>
      ] = await Promise.allSettled([
        getAccountDetails(
          DEALER_SERVICE_URL,
          effectiveUserId,
          token,
          isDealerServiceApiAuthEnable,
          dealerApiHook,
          !isDelegating
        ),
      ]);

      if (accountDetailsResult.status === "fulfilled") {
        if (accountDetailsResult.value.data.length === 0) {
          console.warn(
            "[RBACAccessContext] No dealer accounts found - user may be inactive"
          );
          setHasMappingError(true);
          dispatch(setDataLoadedAction(true));
          return;
        }
      } else if (accountDetailsResult.status === "rejected") {
        console.error(
          "[RBACAccessContext] Failed to fetch dealer accounts:",
          accountDetailsResult.reason
        );
        setHasMappingError(true);
        dispatch(setDataLoadedAction(true));
        return;
      }

      if (ENABLE_MULTIPLE_ACCOUNTS) {
        setPrimaryAccount(
          getPrimaryAccount(accountDetailsResult) as Account | undefined
        );
      }
      dispatch(setAccountDetailsAction(accountDetailsResult));
      dispatch(setDataLoadedAction(true));
    };
    fetchDetails();
  }, [authState, userXid, isDelegating, delegatedUserId]);

  if (canValidateRBACForDealerFacingRole(state, enableFTRBAC)) {
    const rbacDetails: { toggleProcessed: boolean; rbacEnabled: boolean } =
      isRBACEnabledForDealerRoles(state);
    dispatch(
      updateProcessedAction(
        rbacDetails.rbacEnabled,
        ENABLE_CORPORATE_VIEW && rbacDetails.rbacEnabled,
        rbacDetails.toggleProcessed
      )
    );
  }

  return (
    <RBACAccessContext.Provider
      value={{
        userDetails: userDetailsPromise(state),
        featureToggleDetails: featureTogglesPromise(state),
        mappingAccounts: accountTogglesPromise(state),
        primaryAccount,
        isDataLoaded: state.dataLoaded,
        isRBACEnabled: state.rbacEnabled,
        isCorporateViewEnabled: state.corporateViewEnabled,
        hasMappingError,
        hasNoRoles,
      }}
    >
      {children}
    </RBACAccessContext.Provider>
  );
};
